import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import EmailServiceStatusCard from './components/EmailServiceStatusCard';
import ManualEmailTestingPanel from './components/ManualEmailTestingPanel';
import DeliveryTrackingPanel from './components/DeliveryTrackingPanel';
import ApiResponsePanel from './components/ApiResponsePanel';
import EdgeFunctionLogsPanel from './components/EdgeFunctionLogsPanel';
import ErrorAnalysisPanel from './components/ErrorAnalysisPanel';

const EmailTestingDeliveryMonitoringCenter = () => {
  const [activeTab, setActiveTab] = useState('manual_testing');
  const [serviceHealth, setServiceHealth] = useState(null);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadServiceHealth();
    loadDeliveryLogs();
  }, [refreshTrigger]);

  const loadServiceHealth = async () => {
    try {
      setLoading(true);
      
      // Get email statistics from notification_queue
      const { data: allEmails, error } = await supabase
        ?.from('notification_queue')
        ?.select('notification_status, created_at, sent_at, delivered_at, error_message')
        ?.gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString());

      if (error) throw error;

      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const todayEmails = allEmails?.filter(e => new Date(e?.created_at) >= today) || [];

      const stats = {
        dailySendVolume: todayEmails?.length || 0,
        deliverySuccessRate: allEmails?.length > 0 
          ? ((allEmails?.filter(e => e?.notification_status === 'delivered')?.length / allEmails?.length) * 100)?.toFixed(1)
          : 0,
        errorCount: allEmails?.filter(e => e?.notification_status === 'failed')?.length || 0,
        apiHealthStatus: 'operational',
        lastChecked: new Date()?.toISOString()
      };

      setServiceHealth(stats);
    } catch (error) {
      console.error('Error loading service health:', error);
      setServiceHealth({
        dailySendVolume: 0,
        deliverySuccessRate: 0,
        errorCount: 0,
        apiHealthStatus: 'unknown',
        lastChecked: new Date()?.toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryLogs = async () => {
    try {
      const { data, error } = await supabase
        ?.from('notification_queue')
        ?.select(`
          *,
          notification_delivery_log(
            id,
            delivery_channel,
            delivery_status,
            delivered_at,
            error_details
          )
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) throw error;
      setDeliveryLogs(data || []);
    } catch (error) {
      console.error('Error loading delivery logs:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleTestEmailSent = () => {
    handleRefresh();
  };

  const tabs = [
    {
      id: 'manual_testing',
      label: 'Manual Testing',
      icon: 'Send',
      description: 'Send test emails with custom templates'
    },
    {
      id: 'delivery_tracking',
      label: 'Delivery Tracking',
      icon: 'Mail',
      description: 'Real-time email delivery monitoring'
    },
    {
      id: 'api_responses',
      label: 'API Responses',
      icon: 'Code',
      description: 'View Resend API response details'
    },
    {
      id: 'edge_function_logs',
      label: 'Edge Function Logs',
      icon: 'Terminal',
      description: 'Inspect edge function execution traces'
    },
    {
      id: 'error_analysis',
      label: 'Error Analysis',
      icon: 'AlertTriangle',
      description: 'Analyze delivery failures and errors'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Email Testing & Delivery Monitoring Center - PNG HR Analytics</title>
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Icon name="Mail" size={32} className="text-blue-600 dark:text-blue-400" />
                Email Testing & Delivery Monitoring Center
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Test email sending, view Resend API responses, inspect edge function logs, and trace delivery failures
              </p>
            </div>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Icon name="RefreshCw" size={18} />
              Refresh
            </button>
          </div>
        </div>

        {/* Service Health Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <EmailServiceStatusCard
            title="Daily Send Volume"
            value={serviceHealth?.dailySendVolume || 0}
            icon="Send"
            color="blue"
            loading={loading}
            subtitle=""
          />
          <EmailServiceStatusCard
            title="Delivery Success Rate"
            value={`${serviceHealth?.deliverySuccessRate || 0}%`}
            icon="CheckCircle"
            color="green"
            loading={loading}
            subtitle=""
          />
          <EmailServiceStatusCard
            title="Error Count (24h)"
            value={serviceHealth?.errorCount || 0}
            icon="AlertCircle"
            color="red"
            loading={loading}
            subtitle=""
          />
          <EmailServiceStatusCard
            title="Resend API Status"
            value={serviceHealth?.apiHealthStatus === 'operational' ? 'Operational' : 'Unknown'}
            icon="Activity"
            color={serviceHealth?.apiHealthStatus === 'operational' ? 'green' : 'yellow'}
            loading={loading}
            subtitle=""
          />
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-neutral-200 dark:border-slate-700 mb-6">
          <div className="border-b border-neutral-200 dark:border-slate-700">
            <nav className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400' :'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'manual_testing' && (
              <ManualEmailTestingPanel onTestEmailSent={handleTestEmailSent} />
            )}
            {activeTab === 'delivery_tracking' && (
              <DeliveryTrackingPanel deliveryLogs={deliveryLogs} onRefresh={handleRefresh} />
            )}
            {activeTab === 'api_responses' && (
              <ApiResponsePanel deliveryLogs={deliveryLogs} />
            )}
            {activeTab === 'edge_function_logs' && (
              <EdgeFunctionLogsPanel />
            )}
            {activeTab === 'error_analysis' && (
              <ErrorAnalysisPanel deliveryLogs={deliveryLogs} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTestingDeliveryMonitoringCenter;